import { useState, useRef, useEffect, useCallback } from "react";
import { useCreateIOTTestCase, useGetServices } from "../../../../hooks/API/wizard.api";
import SelectionScreen from "./SelectionScreen";
import ServiceStepModal from "./ServiceStepModal";
import SliderScreen, { ActionButtons, SequencePanel } from "./SliderScreem";
import { AXES } from "./ArmDiagram";
import { useOnboarding } from "../../../../context/OnboardingContext";
import { toast } from "react-toast";


const DEFAULT_VALUES = {
  ...Object.fromEntries(AXES.map((a) => [a.id, 90])),
  gripper: 180, 
};


// ── Main Modal ────────────────────────────────────────────────────────────────
const Step9IotModal = ({ onClose, onCancel, projectId, testCase , onSave,initialSteps}) => {
  const [mode, setMode]               = useState(null);
  const [values, setValues]           = useState({ ...DEFAULT_VALUES });
  const [interval, setInterval]       = useState(500);
    const [steps, setSteps] = useState(() => {
    return initialSteps.map((step, i) => ({
      id: i,                         
      is_physical: step.is_physical,
      ...(step.is_physical
        ? { joint_values: step.joint_values, interval: step.interval }
        : { service: {
            service_id:   step.service_id,
            service_uuid: step.service_uuid,
            char_id:      step.char_id,
            char_uuid:    step.char_uuid,
            can_read:     step.can_read,
            can_write:    step.can_write,
            can_notify:   step.can_notify,
          }, interval: step.interval
        }
      )
    }));
  });
  const [confirmModal, setConfirmModal]   = useState(null);
  const [apiStatus, setApiStatus]         = useState(null);
  const [activeAxis, setActiveAxis]       = useState(null);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [moveStatus, setMoveStatus]       = useState(null);
  const [calibrationStatus, setCalibrationStatus] = useState(null);
  const [docbotIp, setDocbotIp]= useState('')
  const [isConnecting , setIsConnecting]= useState(false);

  const { iot, setIot} = useOnboarding();

  const stepIdRef = useRef(initialSteps.length);
  const pendingValuesRef = useRef(null);
  const isSendingRef = useRef(false);
  const moveTimeoutRef = useRef(null);
  const calibrationTimeoutRef = useRef(null);
  const connectionTimeoutRef = useRef(null);

  const { data: servicesData, isLoading: isServicesLoading } = useGetServices({ device_id: iot?.device_id || 1 });
  const { mutate: createIOTTestCase, isLoading: isCreating } = useCreateIOTTestCase();

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") {
        if (showServiceModal) { setShowServiceModal(false); return; }
        mode ? setMode(null) : onCancel();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [mode, onCancel, showServiceModal]);

    const sendMoveRequest = useCallback((currentValues) => {
      isSendingRef.current = true;
      const servos = [
        currentValues.base,
        currentValues.shoulder,
        currentValues.elbow,
        currentValues.wrist_pitch,
        currentValues.wrist_roll,
        currentValues.gripper,
      ];

  console.log('🚀 Sending move request:', servos);
  setMoveStatus('sending');

  window.postMessage(
    {
      source: "ROBITEST_CLOUD",
      action: "DOFBOT_MOVE_REQUEST",
      payload: { servos, time_ms: interval },
    },
    "*"
  );

  moveTimeoutRef.current = setTimeout(() => {
    setMoveStatus(null);
    toast.error('Movement timeout. Is the hardware connected?');
  }, 300000);
},[interval]);

    // Handler: DOFBOT Move Result
  const handleMoveResult = useCallback((data) => {
    isSendingRef.current = false;
    clearTimeout(moveTimeoutRef.current);

    if (data.success) {
      setMoveStatus('success');
      console.log('✅ Move succeeded:', data.data);
      setTimeout(() => setMoveStatus(null), 2000);
    } else {
      setMoveStatus('error');
      toast.error(data.error || 'Failed to move DOFBOT');
      setTimeout(() => setMoveStatus(null), 2000);
    }

      if (pendingValuesRef.current) {
    console.log('📤 Sending queued values:', pendingValuesRef.current);
    sendMoveRequest(pendingValuesRef.current);
    pendingValuesRef.current = null; 
    }

  },[sendMoveRequest]);

    // Handler: DOFBOT Calibrate Result
  const handleCalibrateResult = useCallback((data) => {
    clearTimeout(calibrationTimeoutRef.current);

    if (data.success) {
      setCalibrationStatus('success');
      toast.success(data.message || 'Calibration completed successfully!');
      // Reset values to default after calibration
      setValues({ ...DEFAULT_VALUES });
      setInterval(500);
      setConfirmModal(null);
      setTimeout(() => setCalibrationStatus(null), 2000);
    } else {
      setCalibrationStatus('error');
      toast.error(data.message || data.error || 'Calibration failed');
      setTimeout(() => setCalibrationStatus(null), 2000);
    }
  },[]);

    const handleConnectionResult = (data) => {
      clearTimeout(connectionTimeoutRef.current);
  
      if (data.success) {
        toast.success(data.message || 'DOFBOT connected successfully!');
        setIsConnecting(false);
      } else {
        toast.error(data.message || data.error || 'Connection failed. Please check the IP address.');
        setIsConnecting(false);
      }
    };

  // ── Consolidated Event Listener for DOFBOT events ──────────────────────────
  useEffect(() => {
    const handleDofbotEvents = (event) => {
      if (event.data?.source !== "ROBITEST_AGENT") return;

      const eventType = event.data?.type;

      console.log('🤖 DOFBOT Event:', { type: eventType, data: event.data });

      // Route based on event type
      switch (eventType) {
        case 'DOFBOT_MOVE_RESULT':
          handleMoveResult(event.data);
          break;
        case 'DOFBOT_CALIBRATE_RESULT':
          handleCalibrateResult(event.data);
          break;
        case 'DOFBOT_CONNECT_RESULT':
          handleConnectionResult(event.data);
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', handleDofbotEvents);

    return () => {
      window.removeEventListener('message', handleDofbotEvents);
    };
  }, [handleMoveResult,handleCalibrateResult]);





  // ── Add arm step ────────────────────────────────────────────────────────────
  const markArmStep = async () => {
    const step = {
      id: stepIdRef.current++,
      is_physical: true,
      joint_values: { ...values },
      interval,
    };
    setSteps(s => [...s, step]);
    setApiStatus("sending");
    try { await new Promise(r => setTimeout(r, 400)); setApiStatus("ok"); }
    catch { setApiStatus("error"); }
    setTimeout(() => setApiStatus(null), 2000);
  };



  // ── Handle Joint Movement (called on slider blur) ──────────────────────────
  const handleJointMovement = (currentValues) => {
    // Prevent multiple simultaneous move requests
     if (isSendingRef.current) {
      pendingValuesRef.current = currentValues; // queue it
      return;
    }

    sendMoveRequest(currentValues)

    // // Build servos array in correct order: [base, shoulder, elbow, wrist_pitch, wrist_roll, gripper]
    // const servos = [
    //   currentValues.base,
    //   currentValues.shoulder,
    //   currentValues.elbow,
    //   currentValues.wrist_pitch,
    //   currentValues.wrist_roll,
    //   currentValues.gripper,
    // ];

    // setMoveStatus('sending');

    // // Send movement request
    // window.postMessage(
    //   {
    //     source: "ROBITEST_CLOUD",
    //     action: "DOFBOT_MOVE_REQUEST",
    //     payload: {
    //       servos: servos,
    //       time_ms: interval,
    //     }
    //   },
    //   "*"
    // );

    // // Timeout after 5 minutes if no response
    // moveTimeoutRef.current = setTimeout(() => {
    //   setMoveStatus(null);
    //   toast.error('Movement timeout. Is the hardware connected?');
    // }, 300000);
  };

  // ── Add service step ────────────────────────────────────────────────────────
  const addServiceStep = (selected) => {
    const step = {
      id: stepIdRef.current++,
      is_physical: false,
      service: {
        service_id:   selected.service_id,
        service_uuid: selected.service_uuid,
        char_id:      selected.char_id,
        char_uuid:    selected.char_uuid,
        char_desc:    selected.char_desc,
        can_read:     selected.can_read,
        can_write:    selected.can_write,
        can_notify:   selected.can_notify,
      },
    };
    setSteps(s => [...s, step]);
    setShowServiceModal(false);
  };

  const handleReset = () => {
    setConfirmModal({
      message: "Reset all joints back to 90° (neutral)? This will trigger a calibration request to the DOFBOT.",
      onConfirm: () => {
        setCalibrationStatus('sending');
        
        // Send calibration request
        window.postMessage(
          {
            source: "ROBITEST_CLOUD",
            action: "DOFBOT_CALIBRATE_REQUEST"
          },
          "*"
        );

        // Timeout after 5 minutes if no response
        calibrationTimeoutRef.current = setTimeout(() => {
          setCalibrationStatus(null);
          toast.error('Calibration timeout. Is the hardware connected?');
        }, 300000);
      },
    });
  };

  const deleteStep = (id) => setSteps(s => s.filter(st => st.id !== id));
  const moveStep   = (i, dir) => setSteps(s => {
    const a = [...s], j = i + dir;
    if (j < 0 || j >= a.length) return a;
    [a[i], a[j]] = [a[j], a[i]]; return a;
  });

  const handleCreateTestCase = () => {
    const payload = {
      test_name:   testCase?.name?.trim() || "",
      description: testCase?.description?.trim() || "",
      project_id:  projectId,
      priority:    testCase?.priority,
      steps: steps.map((step, index) => {
        if (step.is_physical) {
          return {
            step_number:  index + 1,
            is_physical:  true,
            joint_values: step.joint_values,
            interval:     step.interval,
          };
        } else {
          return {
            step_number:  index + 1,
            is_physical:  false,
            service_id:   step.service.service_id,
            service_uuid: step.service.service_uuid,
            char_id:      step.service.char_id,
            char_uuid:    step.service.char_uuid,
            can_read:     step.service.can_read,
            can_write:    step.service.can_write,
            can_notify:   step.service.can_notify,
            interval:     step.interval || 500,
          };
        }
      }),
    };


    createIOTTestCase(payload, {
                  onSuccess: (data) => {
                    toast.success("Test case created successfully")
                    //  alert("Test case created successfully!");
                     setIot({
                testCaseIds: data.test_case_id
                  ? [...(iot.testCaseIds || []), data.test_case_id]
                  : iot.testCaseIds,
                recordingSessionId: null,
                // steps: [...(iot.steps || []), ...steps],  
              });
                    onClose();
                    onSave && onSave(); 
                  },
      onError:   () => { toast.error('Failed to create test case.'); },
    });
  };

  const modeLabels = { slider: "Slider Control", "3d": "3D Model" };

  const sharedProps = {
    steps, onDeleteStep: deleteStep, onMoveStep: moveStep,
    onReset: handleReset, apiStatus,
    onMarkArmStep: markArmStep,
    onOpenServiceModal: () => setShowServiceModal(true),
    interval, setInterval,
    onJointMovement: handleJointMovement,
    moveStatus,
  };


   const handleConnectDofbot = () => {
      if (!docbotIp.trim()) {
        toast.error('Please enter the DOFBOT IP address');
        return;
      }
  
      setIsConnecting(true);
      
      // Send postMessage to trigger connection
      window.postMessage(
        {
          source: "ROBITEST_CLOUD",
          action: "DOFBOT_CONNECT_REQUEST",
          payload: { ip_address: docbotIp }
        },
        "*"
      );
  
      toast.info('Connecting to DOFBOT... Please wait');
  
      // Timeout after 5 minutes if no response
      connectionTimeoutRef.current = setTimeout(() => {
        setIsConnecting(false);
        toast.error('Connection timeout. Is the hardware online?');
      }, 300000);
    };
  

  return (
    <>
      <style>{`
        @keyframes armModalIn{from{opacity:0;transform:scale(0.97) translateY(10px)}to{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes fadeSlide{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        input[type=range]::-webkit-slider-thumb{appearance:none;width:16px;height:16px;border-radius:50%;background:var(--c,#3B82F6);border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.18);cursor:pointer;transition:transform 0.1s}
        input[type=range]::-webkit-slider-thumb:hover{transform:scale(1.25)}
        input[type=range]::-moz-range-thumb{width:16px;height:16px;border-radius:50%;background:var(--c,#3B82F6);border:2px solid white;cursor:pointer}
        input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{opacity:1}
      `}</style>

      <div onClick={onCancel} style={{ position:"fixed",inset:0,background:"rgba(15,23,42,0.5)",zIndex:200,backdropFilter:"blur(4px)" }}/>

      <div style={{ position:"fixed",inset:0,zIndex:201,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px",pointerEvents:"none" }}>
        <div style={{ background:"#f8fafc",borderRadius:16,width:"100%",maxWidth:940,maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 80px rgba(0,0,0,0.2)",border:"1px solid #e2e8f0",pointerEvents:"all",overflow:"hidden",animation:"armModalIn 0.22s ease" }}>

          {/* Header */}
          <div style={{ background:"white",borderBottom:"1px solid #e2e8f0",padding:"14px 22px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0 }}>
            <div style={{ display:"flex",alignItems:"center",gap:10 }}>
              {mode && (
                <button onClick={() => setMode(null)}
                  style={{ width:32,height:32,borderRadius:8,border:"1px solid #e2e8f0",background:"white",color:"#374151",cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600 }}>
                  ←
                </button>
              )}
              <div>
                <div style={{ fontSize:15,fontWeight:700,color:"#0f172a",display:"flex",alignItems:"center",gap:8 }}>
                  Robotic Arm – Joint Control
                  {mode && <span style={{ fontSize:11,fontWeight:500,color:"#64748b",background:"#f1f5f9",borderRadius:20,padding:"2px 10px" }}>{modeLabels[mode]}</span>}
                </div>
                <div style={{ fontSize:12,color:"#64748b",marginTop:1 }}>
                  {mode
                    ? `${steps.filter(s=>s.is_physical).length} arm · ${steps.filter(s=>!s.is_physical).length} service · ${steps.length} total`
                    : "Select a control mode to get started"}
                </div>
              </div>
            </div>
            <div style={{ display:"flex",gap:8,alignItems:"center" }}>
              <button onClick={onCancel} style={{ width:32,height:32,borderRadius:8,border:"1px solid #e2e8f0",background:"white",color:"#64748b",cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center" }}>×</button>
            </div>
          </div>
            
       {/* DOFBOT Connection Bar */}
          <div style={{ background: "white", borderBottom: "1px solid #e2e8f0", padding: "10px 22px", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              <span style={{ fontSize: 15 }}>🔗</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#374151", whiteSpace: "nowrap" }}>DOFBOT IP</span>
            </div>
            <div style={{ width: 1, height: 20, background: "#e2e8f0", flexShrink: 0 }} />
            <input
              type="text"
              placeholder="e.g. 192.168.1.15"
              value={docbotIp}
              onChange={(e) => setDocbotIp(e.target.value)}
              disabled={isConnecting}
              style={{
                flex: 1,
                padding: "7px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                fontSize: 13,
                color: "#0f172a",
                background: isConnecting ? "#f8fafc" : "white",
                outline: "none",
                fontFamily: "monospace",
                minWidth: 0,
                opacity: isConnecting ? 0.6 : 1,
              }}
            />
            <button
              onClick={handleConnectDofbot}
              disabled={isConnecting || !docbotIp.trim()}
              style={{
                padding: "7px 16px",
                background: isConnecting || !docbotIp.trim() ? "#e2e8f0" : "#16a34a",
                border: "none",
                color: isConnecting || !docbotIp.trim() ? "#94a3b8" : "white",
                borderRadius: 8,
                cursor: isConnecting || !docbotIp.trim() ? "default" : "pointer",
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexShrink: 0,
              }}
            >
              {isConnecting ? "⚙️ Connecting…" : "Connect"}
            </button>
            <span style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap", flexShrink: 0 }}>
              Enter the IP of your DOFBOT to connect
            </span>
          </div>

          {/* Body */}
          {!mode && (
            <div style={{ animation:"fadeSlide 0.18s ease" }}>
              <SelectionScreen onSelect={setMode} />
            </div>
          )}

          {mode === "slider" && (
            <div style={{ display:"contents", animation:"fadeSlide 0.18s ease" }}>
              <SliderScreen
                values={values} setValues={setValues}
                activeAxis={activeAxis} setActiveAxis={setActiveAxis}
                {...sharedProps}
              />
            </div>
          )}

          {mode === "3d" && (
            <div style={{ display:"contents", animation:"fadeSlide 0.18s ease" }}>
              <ThreeDArm {...sharedProps} />
            </div>
          )}

          {/* Footer */}
          <div style={{ background:"white",borderTop:"1px solid #e2e8f0",padding:"13px 22px",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0 }}>
            <span style={{ fontSize:12,color:"#94a3b8" }}>
              Press <kbd style={{ background:"#f1f5f9",border:"1px solid #e2e8f0",borderRadius:4,padding:"1px 6px",fontSize:11,color:"#475569" }}>Esc</kbd> {mode ? "to go back" : "to close"}
            </span>
            <div style={{ display:"flex",gap:9 }}>
              <button onClick={onCancel}
                style={{ padding:"8px 18px",background:"white",border:"1px solid #e2e8f0",color:"#374151",borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:500 }}>
                Cancel
              </button>
              <button
                onClick={handleCreateTestCase}
                disabled={steps.length === 0 || isCreating}
                style={{ padding:"8px 20px",background:steps.length===0?"#e2e8f0":"#3B82F6",border:"none",color:steps.length===0?"#94a3b8":"white",borderRadius:8,cursor:steps.length===0?"default":"pointer",fontSize:13,fontWeight:600 }}>
                {isCreating ? "Saving…" : `Save & Close${steps.length > 0 ? ` (${steps.length} step${steps.length !== 1 ? "s" : ""})` : ""}`}
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Service step modal */}
      {showServiceModal && (
        <ServiceStepModal
          services={servicesData}
          onSelect={addServiceStep}
          onClose={() => setShowServiceModal(false)}
        />
      )}

      {/* Confirm reset modal */}
      {confirmModal && (
        <ConfirmModal
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}
    </>
  );
};

export default Step9IotModal;



// ── 3D Canvas Arm ─────────────────────────────────────────────────────────────
const ThreeDArm = ({
  onMarkArmStep, apiStatus, steps, onDeleteStep, onMoveStep, onReset,
  onOpenServiceModal, interval, setInterval,
}) => {
  const canvasRef = useRef(null);
  const stateRef  = useRef({ rotX: 18, rotY: -35, joints: { ...DEFAULT_VALUES } });
  const dragRef   = useRef({ active: false, lx: 0, ly: 0 });
  const rafRef    = useRef(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    const bg = ctx.createLinearGradient(0, 0, W * 0.3, H);
    bg.addColorStop(0, "#0d1b2e");
    bg.addColorStop(1, "#111827");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    ctx.strokeStyle = "rgba(148,163,184,0.06)";
    ctx.lineWidth = 1;
    for (let x = 0; x < W; x += 36) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,H); ctx.stroke(); }
    for (let y = 0; y < H; y += 36) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke(); }

    const { rotX, rotY, joints } = stateRef.current;
    const rx = (rotX * Math.PI) / 180;
    const ry = (rotY * Math.PI) / 180;

    const project = ([x, y, z]) => {
      const x1 =  x * Math.cos(ry) + z * Math.sin(ry);
      const z1 = -x * Math.sin(ry) + z * Math.cos(ry);
      const y2 =  y * Math.cos(rx) - z1 * Math.sin(rx);
      const z2 =  y * Math.sin(rx) + z1 * Math.cos(rx);
      const fov = 600, cx = W/2, cy = H/2 + 30;
      const s = fov / (fov + z2 + 200);
      return [cx + x1 * s, cy - y2 * s, s];
    };

    const seg = (p1, p2, col, w, glow = false) => {
      const [x1,y1,s1] = project(p1), [x2,y2,s2] = project(p2);
      const s = (s1+s2)/2;
      ctx.beginPath(); ctx.moveTo(x1,y1); ctx.lineTo(x2,y2);
      ctx.strokeStyle = col; ctx.lineWidth = w * s * 2.2; ctx.lineCap = "round";
      if (glow) { ctx.shadowColor = col; ctx.shadowBlur = 16; }
      ctx.stroke(); ctx.shadowBlur = 0;
    };

    const joint = (p, col, r = 8) => {
      const [px,py,s] = project(p);
      ctx.beginPath(); ctx.arc(px, py, r*s*2.6, 0, Math.PI*2);
      ctx.fillStyle = col+"22"; ctx.strokeStyle = col; ctx.lineWidth = 1.8; ctx.fill(); ctx.stroke();
      ctx.beginPath(); ctx.arc(px, py, r*s*1.4, 0, Math.PI*2); ctx.fillStyle = col; ctx.fill();
      ctx.beginPath(); ctx.arc(px, py, r*s*0.5, 0, Math.PI*2); ctx.fillStyle = "white"; ctx.fill();
    };

    const toRad = (deg) => ((deg - 90) / 90) * (Math.PI * 0.5);
    const bRot = toRad(joints.base);
    const sA   = toRad(joints.shoulder) * 0.85;
    const eA   = toRad(joints.elbow)    * 0.9;
    const wA   = toRad(joints.wrist_pitch) * 0.6;
    const gW   = 14 + (joints.gripper / 180) * 22;

    ctx.beginPath();
    const [bpx, bpy] = project([0, 100, 0]);
    ctx.ellipse(bpx, bpy, 62 * (project([0,100,0])[2]), 18 * (project([0,100,0])[2]), 0, 0, Math.PI*2);
    ctx.fillStyle = "rgba(51,65,85,0.8)"; ctx.strokeStyle = "#475569"; ctx.lineWidth = 1.5; ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.ellipse(bpx, bpy, 50*(project([0,100,0])[2]), 14*(project([0,100,0])[2]), 0, 0, Math.PI*2);
    ctx.strokeStyle = "#64748b44"; ctx.lineWidth = 1; ctx.stroke();

    const colB = [0, 100, 0], colT = [0, 55, 0];
    seg(colB, colT, "#334155", 9);
    seg(colB, colT, "#475569", 5);

    const sp = [Math.sin(bRot)*2, 55, Math.cos(bRot)*2];
    const uaL = 88;
    const ep = [
      sp[0] + Math.sin(bRot) * Math.cos(sA) * uaL,
      sp[1] - Math.cos(sA) * uaL * 0.88 + Math.sin(sA) * 10,
      sp[2] + Math.cos(bRot) * Math.cos(sA) * uaL,
    ];
    const faL = 72;
    const cA = sA + eA;
    const fp = [
      ep[0] + Math.sin(bRot) * Math.cos(cA) * faL,
      ep[1] - Math.cos(cA) * faL * 0.88,
      ep[2] + Math.cos(bRot) * Math.cos(cA) * faL,
    ];
    const wrL = 32;
    const tA = cA + wA;
    const wp = [
      fp[0] + Math.sin(bRot) * Math.cos(tA) * wrL,
      fp[1] - Math.cos(tA) * wrL * 0.88,
      fp[2] + Math.cos(bRot) * Math.cos(tA) * wrL,
    ];

    seg(colT, sp, "#334155", 7);
    seg(sp, ep, "#2d3748", 16); seg(sp, ep, "#8B5CF6", 11, true);
    seg(ep, fp, "#1e293b", 13); seg(ep, fp, "#06B6D4",  8, true);
    seg(fp, wp, "#1e293b",  9); seg(fp, wp, "#10B981",  6, true);

    const gDir = [Math.sin(bRot)*Math.cos(tA), -Math.cos(tA)*0.88, Math.cos(bRot)*Math.cos(tA)];
    const gPerp = [-gDir[2], 0, gDir[0]];
    const mag = Math.sqrt(gPerp[0]**2 + gPerp[2]**2) || 1;
    const gN = gPerp.map(v => v / mag);
    for (const s of [-1, 1]) {
      const fBase = wp.map((v,i) => v + s * (gW/2) * gN[i]);
      const fTip  = fBase.map((v,i) => v + gDir[i] * 44);
      seg(fBase, fTip, "#EF4444", 5, true);
      joint(fTip, "#EF4444", 3.5);
    }

    joint(sp, "#3B82F6", 9);
    joint(ep, "#8B5CF6", 8);
    joint(fp, "#06B6D4", 7);
    joint(wp, "#10B981", 5.5);

    ctx.beginPath(); ctx.ellipse(bpx, bpy + 2, 58*(project([0,100,0])[2]), 8*(project([0,100,0])[2]), 0, 0, Math.PI*2);
    ctx.fillStyle = "rgba(0,0,0,0.3)"; ctx.fill();

    AXES.forEach((axis, i) => {
      const val = joints[axis.id];
      const bx2 = 14, by2 = 14 + i * 20;
      ctx.fillStyle = axis.color + "28";
      ctx.beginPath(); ctx.roundRect(bx2, by2, 90, 14, 3); ctx.fill();
      ctx.fillStyle = axis.color;
      ctx.beginPath(); ctx.roundRect(bx2, by2, (val/180)*90, 14, 3); ctx.fill();
      ctx.font = "bold 9px monospace"; ctx.fillStyle = "rgba(226,232,240,0.85)";
      ctx.fillText(`${axis.short}  ${val}°`, bx2 + 95, by2 + 10);
    });

    ctx.font = "11px system-ui"; ctx.fillStyle = "rgba(148,163,184,0.45)";
    ctx.fillText("Drag to orbit · Scroll to zoom", W - 185, H - 12);
  }, []);

  useEffect(() => {
    const loop = () => { draw(); rafRef.current = requestAnimationFrame(loop); };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [draw]);

  const onMD = (e) => { dragRef.current = { active: true, lx: e.clientX, ly: e.clientY }; };
  const onMM = (e) => {
    if (!dragRef.current.active) return;
    stateRef.current.rotY += (e.clientX - dragRef.current.lx) * 0.55;
    stateRef.current.rotX += (e.clientY - dragRef.current.ly) * 0.55;
    stateRef.current.rotX = Math.max(-70, Math.min(70, stateRef.current.rotX));
    dragRef.current.lx = e.clientX; dragRef.current.ly = e.clientY;
  };
  const onMU = () => { dragRef.current.active = false; };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "18px 20px", flex: 1, overflowY: "auto" }}>
      <div style={{ borderRadius: 14, overflow: "hidden", border: "1px solid #1e293b", position: "relative", background: "#0d1b2e" }}>
        <canvas ref={canvasRef} width={860} height={400}
          style={{ width: "100%", height: "auto", display: "block", cursor: dragRef.current?.active ? "grabbing" : "grab" }}
          onMouseDown={onMD} onMouseMove={onMM} onMouseUp={onMU} onMouseLeave={onMU}
        />
        <div style={{ position: "absolute", top: 10, right: 12, display: "flex", gap: 6 }}>
          <span style={{ background: "rgba(15,23,42,0.8)", border: "1px solid #334155", borderRadius: 6, padding: "3px 10px", fontSize: 10, color: "#64748b", fontFamily: "monospace" }}>3D INTERACTIVE</span>
        </div>
      </div>

      {/* Interval for 3D mode */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, background: "white", border: "1px solid #e2e8f0", borderRadius: 9, padding: "10px 14px" }}>
        <label style={{ fontSize: 12, color: "#64748b", fontWeight: 500, whiteSpace: "nowrap" }}>Interval (ms)</label>
        <input
          type="number" min={0} max={10000} step={50} value={interval}
          onChange={e => setInterval(Number(e.target.value))}
          style={{ width: 90, padding: "6px 10px", borderRadius: 7, border: "1px solid #e2e8f0", fontSize: 13, color: "#0f172a", outline: "none", background: "white" }}
        />
      </div>

      {/* Action buttons */}
      <ActionButtons
        onMarkArmStep={onMarkArmStep}
        onOpenServiceModal={onOpenServiceModal}
        onReset={onReset}
        apiStatus={apiStatus}
      />

      {/* Unified sequence */}
      <SequencePanel steps={steps} onDeleteStep={onDeleteStep} onMoveStep={onMoveStep} />
    </div>
  );
};



// ── Confirm Modal ─────────────────────────────────────────────────────────────
const ConfirmModal = ({ message, onConfirm, onCancel }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(3px)" }}>
    <div style={{ background: "white", borderRadius: 12, padding: "28px 32px", maxWidth: 360, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", border: "1px solid #e2e8f0" }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: "#0f172a", marginBottom: 8 }}>Confirm Reset</div>
      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 24, lineHeight: 1.6 }}>{message}</div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={{ padding: "8px 18px", background: "white", border: "1px solid #e2e8f0", color: "#64748b", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 500 }}>Cancel</button>
        <button onClick={onConfirm} style={{ padding: "8px 18px", background: "#3B82F6", border: "none", color: "white", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Reset</button>
      </div>
    </div>
  </div>
);
